import '../App.css';
import './css/LoginLandingPage.css';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeButton } from '../components/website/HomeButton';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/apiConfig';
import { useAuth } from '../components/router/authContext.jsx';
import DOMPurify from 'dompurify';

axios.defaults.withCredentials = true;

function LoginLanding() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const [newUserUsername, setNewUserUsername] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
    const [newUserFirstName, setNewUserFirstName] = useState('');
    const [newUserLastName, setNewUserLastName] = useState('');
    const [newUserCompanyName, setNewUserCompanyName] = useState('');
    const [newUserPhoneNumber, setNewUserPhoneNumber] = useState('');
    const [newUserTermsAndConditions, setNewUserTermsAndConditions] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsContent, setTermsContent] = useState('');


    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [loginState, setLoginState] = useState('login');
    const [email, setEmail] = useState('');

    // ACT Free Trial Token
    // TODO: Remove after conference
    const [freeTrialToken, setFreeTrialToken] = useState('');
    const apiCallInProgress = useRef(false);
    const tokenValidationComplete = useRef(false);

    // Loading state
    const [loading, setLoading] = useState(false);
    const [tokenValidated, setTokenValidated] = useState(false);

    const errorTimeoutRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, isLoading } = useAuth();

    const clearError = () => {
        setError('');
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    };

    const validateFreeTrialToken = async (token) => {
        if (apiCallInProgress.current || tokenValidationComplete.current || !token) {
            return;
        }

        apiCallInProgress.current = true;

        try {
            setLoading(true);
            const response = await axios.post(API_ENDPOINTS.tokenValidationACT, {
                token: freeTrialToken,
            });

            console.log("Response:", response.data);
            if (response.data.email) {
                setTokenValidated(true);
                console.log("Email filled", response.data.email);
                // Pre-fill email if it's included in the token data
                setNewUserEmail(response.data.email);
            } else {
                setError("This invitation link is invalid or has expired.");
            }
        } catch (err) {
            setError("Unable to validate your invitation. Please try again later.");
            console.error("Token validation error:", err);
        } finally {
            setLoading(false);
        }
    };

    // Check for free trial token
    useEffect(() => {
        if (tokenValidationComplete.current) {
            return;
        }

        console.log("Checking for free trial token");
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');
        console.log("Token:", token);
        if (token && !apiCallInProgress.current) {
            setLoginState('register');
            setFreeTrialToken(token);
            console.log("freeTrialToken:", freeTrialToken);
            validateFreeTrialToken(freeTrialToken);
        }
    }, [location, freeTrialToken]);

    useEffect(() => {
        if (error) {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            errorTimeoutRef.current = setTimeout(() => {
                setError('');
                errorTimeoutRef.current = null;
            }, 15000);
        }

        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, [error]);

    // Terms and conditions
    useEffect(() => {
        if (showTermsModal) {
            fetch('/terms-and-conditions.html')
                .then(response => response.text())
                .then(html => {
                    const sanitizedHtml = DOMPurify.sanitize(`<div>${html}</div>`);
                    setTermsContent(sanitizedHtml);
                    console.log("Fetched and sanitized terms and conditions");
                })
                .catch((error) => {
                    console.error('Error fetching terms', error);
                })

        }
    }, [showTermsModal]);

    useEffect(() => {
        const handleUserInteraction = () => {
            if (error) {
                clearError();
            }
        };

        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);

        return () => {
            document.removeEventListener('click', handleUserInteraction);
            document.removeEventListener('keydown', handleUserInteraction);
        };
    }, [error]);

    useEffect(() => {
        // Check for query parameters when component mounts
        const queryParams = new URLSearchParams(location.search);
        const mode = queryParams.get('mode');

        if (mode === 'register') {
            setLoginState('register');
        }
    }, [location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        console.log('Login attempt initiated for username:', username);
        console.log('Full login URL:', API_ENDPOINTS.login);

        try {
            console.log('Making login request to:', API_ENDPOINTS.login);
            const response = await axios.post(API_ENDPOINTS.login, {
                username,
                password
            });
            console.log('Login successful, received token:', response.data.token ? 'Token exists' : 'No token received');

            // Store token in localStorage (this could also be done in the login function)
            localStorage.setItem('authToken', response.data.token);

            // Update auth context with user data
            await login({
                token: response.data.token,
                user: response.data.user || { username } // Use provided user data or create minimal user object
            });

            setError('');

            // Navigate based on subscription status
            if (response.data.subscription_status === "active") {
                navigate('/fleet-planning-pro');
            } else {
                navigate('/subscription');
            }
        } catch (error) {
            console.error('Login failed:', error.response?.data || error.message);
            setError('Invalid username or password');
        }
    }

    const handleRegister = async (e) => {
        e.preventDefault();
        console.log('Register attempt initiated for username:', newUserUsername);
        console.log('Full register URL:', API_ENDPOINTS.register);

        try {
            if (newUserPassword !== newUserConfirmPassword) {
                setError('Passwords do not match');
                return;
            }

            const response = await axios.post(API_ENDPOINTS.register, {
                username: newUserUsername,
                email: newUserEmail,
                password: newUserPassword,
                confirm_password: newUserConfirmPassword,
                first_name: newUserFirstName,
                last_name: newUserLastName,
                company_name: newUserCompanyName,
                phone_number: newUserPhoneNumber,
                token: freeTrialToken,
                has_token: tokenValidated
            });
            console.log('Registration successful, received token:', response.data.token ? 'Token exists' : 'No token received');

            localStorage.setItem('authToken', response.data.token);
            setError('');
            console.log('Navigating to home page');
            if (tokenValidated) {
                navigate('/fleet-planning-pro');
            } else {
                setLoginState('register-success');
            }
        } catch (error) {
            console.error('Registration failed:', error.response?.data || error.message);
            
            // Extract specific error message from backend response
            if (error.response && error.response.data && error.response.data.error) {
                setError(error.response.data.error);
            } else {
                setError('Registration failed. Please try again later.');
            }
        }
    }

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        console.log('Forgot password attempt initiated for email:', email);

        try {
            const response = await axios.post(API_ENDPOINTS.forgotPassword, {
                email
            });
            console.log('Password reset email sent successfully');
            setStatus('Password reset email sent successfully, please check your email for a link to reset your password.');
        } catch (error) {
            console.error('Password reset failed:', error.response?.data || error.message);
            setError('Failed to send password reset email');
        }
    }

    const renderLoginForm = () => {
        switch (loginState) {
            case 'login':
                return (
                    <div className="login-form-wrapper">
                        <div className="login-form-container">
                            <div className="login-form-body">
                                <form onSubmit={handleLogin}>
                                    <div className="login-form-group">
                                        <label htmlFor="email">Email or Username</label>
                                        <input type="text" className="form-control" id="email" onChange={(e) => setUsername(e.target.value)} required />
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="password">Password</label>
                                        <input type="password" className="form-control" id="password" onChange={(e) => setPassword(e.target.value)} required />
                                    </div>
                                    <div className="login-button-container">
                                        <button type="submit" className='login-button'>Login</button>
                                    </div>
                                </form>
                                {error && <p className='text-danger'>{error}</p>}
                                <div className="g-signin2" data-onsuccess="onSignIn"></div>

                                <div className="text-center mt-3">
                                    <button className='btn btn-link' onClick={() => setLoginState('forgot')}>Forgot your password?</button>
                                </div>
                                <div className="text-center mt-3">
                                    <div className="login-form-text-container">
                                        <span className='login-form-text'>Don't have an account?</span>
                                        <button className='btn btn-link' onClick={() => setLoginState('register')}>Sign up now</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'register':
                return (
                    <div className="login-form-wrapper">
                        <div className="login-form-container">
                            <div className="login-form-body">
                                <div className='login-form-text-container'>
                                    <p className='login-form-text'>Create an account</p>
                                </div>
                                <form onSubmit={handleRegister}>
                                    <div className='login-name-container'>
                                        <div className="login-form-group">
                                            <label htmlFor="first-name">First Name <span className="required-asterisk">*</span></label>
                                            <input type="text" className="form-control" id="first-name" onChange={(e) => setNewUserFirstName(e.target.value)} required />
                                        </div>
                                        <div className="login-form-group">
                                            <label htmlFor="last-name">Last Name <span className="required-asterisk">*</span></label>
                                            <input type="text" className="form-control" id="last-name" onChange={(e) => setNewUserLastName(e.target.value)} required />
                                        </div>
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="username">Username <span className="required-asterisk">*</span></label>
                                        <input type="text" className="form-control" id="username" onChange={(e) => setNewUserUsername(e.target.value)} required />
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="email" className="email-label">Email <span className="required-asterisk">*</span></label>
                                        {tokenValidated ?
                                            <div className="read-only-container">
                                                <span className="read-only">{newUserEmail}</span>
                                            </div>
                                            :
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="email"
                                                onChange={(e) => setNewUserEmail(e.target.value)}
                                                required
                                            />
                                        }
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="password">Password <span className="required-asterisk">*</span></label>
                                        <input type="password" className="form-control" id="password" onChange={(e) => setNewUserPassword(e.target.value)} required />
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="confirm-password">Confirm Password <span className="required-asterisk">*</span></label>
                                        <input type="password" className="form-control" id="confirm-password" onChange={(e) => setNewUserConfirmPassword(e.target.value)} required />
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="company-name">Company Name</label>
                                        <input type="text" className="form-control" id="company-name" onChange={(e) => setNewUserCompanyName(e.target.value)} />
                                    </div>
                                    <div className="login-form-group">
                                        <label htmlFor="phone-number">Phone Number</label>
                                        <input type="text" className="form-control" id="phone-number" onChange={(e) => setNewUserPhoneNumber(e.target.value)} />
                                    </div>
                                    <div className="required-fields-note">
                                        <span className="required-asterisk">*</span> Required fields
                                    </div>
                                    <div className="terms-and-conditions-container">
                                        <label htmlFor="terms-and-conditions">
                                            <input
                                                type="checkbox"
                                                id="terms-and-conditions"
                                                onChange={(e) => setNewUserTermsAndConditions(e.target.checked)}
                                                style={{ marginRight: '5px' }}
                                            />
                                            I agree to the <span
                                                onClick={() => setShowTermsModal(true)}
                                                style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                                Terms and Conditions
                                            </span>
                                        </label>

                                        {showTermsModal && (
                                            <div className="terms-modal-overlay">
                                                <div className="terms-modal-content">
                                                    <div
                                                        dangerouslySetInnerHTML={{ __html: termsContent }}
                                                        style={{ overflowY: 'auto', padding: '50px' }}
                                                    />
                                                    <div className='terms-button-container'>
                                                        <button onClick={() => setShowTermsModal(false)} className='btn btn-custom terms-button'>Close</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>


                                    <div className="login-button-container">
                                        <button type="submit" className='login-button' disabled={!newUserTermsAndConditions}>Create Account</button>
                                    </div>
                                </form>
                                {error && <p className='text-danger'>{error}</p>}
                                <div className="login-form-text-container" style={{ paddingBottom: '0px'}}>
                                    <span className='login-form-text'>Already have an account?</span>
                                    <button className='btn btn-link' onClick={() => setLoginState('login')}>Back to login</button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'register-success':
                return (
                    <div className="login-form-wrapper">
                        <div className="login-form-container">
                            <div className="login-form-body">
                                <div className='login-form-text-container'>
                                    <p className='login-form-text'>Registration successful! Please check your email for a verification link.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'forgot':
                return (
                    <div className="login-form-wrapper">
                        <div className="login-form-container">
                            <div className="login-form-body">
                                <div className='login-form-text-container'>
                                    <p className='login-form-text'>Forgot your password? Enter your email below and we will send you a link to reset your password.</p>
                                </div>
                                <form onSubmit={handleForgotPassword}>
                                    <div className="login-form-group">
                                        <label htmlFor="email">Email</label>
                                        <input type="email" className="form-control" id="email" placeholder="e.x. johndoe@gmail.com" onChange={(e) => setEmail(e.target.value)} required />
                                    </div>
                                    <div className="login-button-container">
                                        <button type="submit" className='login-button'>Reset Password</button>
                                    </div>
                                </form>
                                {error && <p className='text-danger'>{error}</p>}
                                {status && <p className='text-success'>{status}</p>}
                                <div className="login-form-text-container">
                                    <button className='btn btn-link' onClick={() => setLoginState('login')}>Back to login</button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        };
    }

    return (
        <div className='login-landing-container'>
            <script src="https://apis.google.com/js/platform.js" async defer></script>
            <div className='login-background-half'>
                <img src={'/img/truck-fleet-login.svg'} alt="EV Truck Fleet" style={{ maxWidth: '500px' }}/>
                <div className='login-image-overlay'>
                    <p>Empower your fleet</p>
                </div>
            </div>
            <div className='login-form-half'>
                <div className='login-home-button-container'>
                    <HomeButton />
                </div>
                {renderLoginForm()}
            </div>
        </div>
    );
}

export default LoginLanding;